import { getExpertById } from '@api/experts';
import Modal from '@components/Modal';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { ExpertInterface } from '@interfaces/index';
import { Backdrop, CircularProgress } from '@mui/material';
import { MotionImage } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import { truncateString } from '@utils/string.util';
import classNames from 'classnames';
import { castResponse } from 'custom.d';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router';
import { ExpertBookingAvailability } from '../ExpertBookingAvailability/ExpertBookingAvailability';
import styles from './index.module.scss';

export interface ExpertCardProps {
  id: number;
  image: string;
  name: string;
  description: string;
  experience: string;
}

export const ExpertCard: FunctionComponent<ExpertCardProps> = (props) => {
  const { id, image, name, description, experience } = props;
  const history = useHistory();
  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  const [expertDetails, setExpertDetails] = useState<ExpertInterface | null>(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Store previous focus element to restore focus when modal closes
  const previousFocusElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (id && availabilityOpen) {
      getExpertDetails();
      previousFocusElement.current = document.activeElement as HTMLElement;
    }
  }, [id, availabilityOpen]);

  useEffect(() => {
    if (availabilityOpen && closeButtonRef.current) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    } else if (!availabilityOpen && previousFocusElement.current) {
      // Restore focus when modal closes
      previousFocusElement.current.focus();
    }
  }, [availabilityOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (availabilityOpen && event.key === KeyConfigurations.ESCAPE) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [availabilityOpen]);

  const getExpertDetails = async () => {
    try {
      handleOpenDrawer();
      const response = await getExpertById(Number(id));
      if (response.success) {
        const data = castResponse<ExpertInterface>(response);
        setExpertDetails(data);
      } else {
        setExpertDetails(null);
      }
    } catch {
      setExpertDetails(null);
    } finally {
      handleCloseDrawer();
    }
  };

  const handleCloseModal = () => {
    setAvailabilityOpen(false);
    setExpertDetails(null);
  };

  const truncatedDescription = truncateString(description.split('\\n').join(' '), 300);

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const navigateToExpertProfile = () => {
    history.push(`/expert/${id}`);
  };

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openDrawer}
        onClick={handleCloseDrawer}
        aria-label="Loading expert details"
      >
        <CircularProgress color="inherit" aria-label="Loading" />
      </Backdrop>
      {availabilityOpen &&
        expertDetails &&
        ReactDOM.createPortal(
          <Modal
            open={availabilityOpen}
            setOpen={() => {
              setAvailabilityOpen(false);
              setExpertDetails(null);
            }}
            className={styles.modalRoot}
            overlay
            aria-labelledby="expert-booking-modal-title"
            aria-describedby="expert-booking-modal-description"
            role="dialog"
          >
            <div className={styles.btnWrapper}>
              <div className={styles.btnClose}>
                <button
                  type="button"
                  onClick={() => {
                    setAvailabilityOpen(false);
                    setExpertDetails(null);
                  }}
                  aria-label="Close booking modal"
                >
                  Close&nbsp;
                  <i
                    className={classNames('icon', 'icon-x', styles.btnCloseIcon)}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
            <div
              className={styles.leftSideExpertDetailsDesktop}
              ref={modalContentRef}
              id="expert-booking-modal-description"
            >
              <MotionImage
                src={`https://${image}`}
                alt={`${name}, expert profile picture`}
                className={styles.expertProfileImg}
                shape="circle"
                skeletonHeight="100px"
                skeletonWidth="100px"
              />
              <div className={styles.expertDetails}>
                <h5 id="expert-booking-modal-title">{`${expertDetails.firstName} ${expertDetails.lastName}`}</h5>

                {expertDetails.expertDetail.fullDescription.split('\\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>

            <div className={styles.leftSideExpertDetailsGamma} aria-hidden="true">
              <div className={styles.row}>
                <MotionImage
                  src={`https://${image}`}
                  alt={name}
                  className={styles.expertProfileImg}
                  shape="circle"
                  skeletonHeight="100px"
                  skeletonWidth="100px"
                />
                <h5>{`${expertDetails.firstName} ${expertDetails.lastName}`}</h5>
              </div>

              <div className={styles.expertDetails}>
                {expertDetails.expertDetail.fullDescription.split('\\n').map((line, index) => (
                  <p key={index}>{line} </p>
                ))}
              </div>
            </div>

            <hr aria-hidden="true" />

            <ExpertBookingAvailability expertDetails={expertDetails} />
          </Modal>,
          document.body,
        )}

      <article className={styles.mainContainer} aria-labelledby={`expert-name-${id}`}>
        <div
          className={styles.subContainer}
          onClick={navigateToExpertProfile}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigateToExpertProfile();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`View full profile of ${name}`}
        >
          <MotionImage
            src={`https://${image}`}
            alt={`${name}, expert profile`}
            className={styles.expertProfileImg}
          />
          <h5 className={styles.name} id={`expert-name-${id}`}>
            {name}
          </h5>
          <p className={styles.experience}>{experience}</p>

          <p className={styles.description}>
            {description.split('\\n').map((line, index) => (
              <p key={index} className={styles.description}>
                {line}
              </p>
            ))}
          </p>
        </div>

        <button
          className={styles.viewProfile}
          onClick={() => history.push(`/expert/${id}`)}
          aria-label={`View full profile of ${name}`}
        >
          <p>view full profile</p>
        </button>
        <div className={styles.bookButtonContainer}>
          <div className={styles.bookButton}>
            <SystemButton
              onClick={() => setAvailabilityOpen(true)}
              aria-haspopup="dialog"
              aria-label={`Book a session with ${name}`}
            >
              Book a session
            </SystemButton>
          </div>
        </div>
      </article>
    </>
  );
};

ExpertCard.displayName = 'ExpertCard';
